import { type ReactNode } from 'react';

import { cn } from '../../lib/cn';
import { decorativeIcon, type LucideIcon } from '../../lib/icons';
import { ConnectivityBand } from '../ConnectivityBand/ConnectivityBand';
import { Readout } from '../Readout/Readout';
import { Wordmark } from '../Wordmark/Wordmark';

export interface WorkerNavItem {
  readonly key: string;
  readonly href: string;
  /** Already translated. ALWAYS rendered — never an icon-only tab. */
  readonly label: string;
  readonly icon: LucideIcon;
  readonly current?: boolean;
}

export interface AppShellWorkerProps {
  /** The wordmark's accessible name, already translated. */
  readonly brandLabel: string;
  /** The real SVG once the client supplies one. */
  readonly brandSrc?: string;
  /**
   * The site clock — "05 h 48", already formatted in the SITE's timezone by
   * the caller, with `zone` carrying the abbreviation.
   */
  readonly clock?: { readonly time: string; readonly zone: string };

  /**
   * Connectivity is a SHELL concern, not a home-screen one. A worker who
   * navigates to "Mes quarts" with three punches queued must still see that
   * they are queued, so the band renders on every screen.
   */
  readonly online: boolean;
  readonly queueCount: number;
  readonly connectivityLabels: {
    readonly state: string;
    readonly queue?: string;
    readonly clear?: string;
  };

  /** A `Banner` for a blocking condition, under the connectivity band. */
  readonly banner?: ReactNode;

  readonly nav: readonly WorkerNavItem[];
  /** The nav's accessible name, already translated. */
  readonly navLabel: string;
  /** Lets an app inject its router's link. Defaults to a plain `<a>`. */
  readonly renderNavLink?: (
    item: WorkerNavItem,
    className: string,
    children: ReactNode,
  ) => ReactNode;

  /**
   * The punch key. Pinned directly above the nav, in the thumb zone, and
   * NEVER scrolled out of view — in any orientation, in any state.
   */
  readonly primaryAction?: ReactNode;

  /** Skips to the main region. Already translated. */
  readonly skipLabel: string;
  readonly children: ReactNode;
  readonly className?: string;
}

/**
 * The worker PWA shell: header rail, connectivity band, scrolling main, pinned
 * primary action, bottom navigation.
 *
 * The vertical contract, from DESIGN.md's Layout section:
 *
 *   header rail 64 · connectivity band 48 · main (flex, scrolls) ·
 *   primary key 112 · bottom nav 68 + safe area
 *
 * The middle flexes and truncates. The header, band, key and nav never do —
 * that is what stops the punch key sliding off a 360 px screen once a French
 * shift name wraps to three lines.
 *
 * **Landscape.** Portrait is the manifest orientation, but landscape must not
 * break: sideways — short AND landscape — the rail collapses to 48 px and the
 * layout keeps the key above the nav. The key is never scrolled away.
 *
 * The FR/EN toggle is deliberately absent: it belongs on the login screen
 * (before authentication) and on the profile screen. In the rail it would sit
 * one thumb-width from the punch key.
 */
export function AppShellWorker({
  brandLabel,
  brandSrc,
  clock,
  online,
  queueCount,
  connectivityLabels,
  banner,
  nav,
  navLabel,
  renderNavLink,
  primaryAction,
  skipLabel,
  children,
  className,
}: AppShellWorkerProps) {
  return (
    <div
      className={cn(
        'flex h-dvh min-h-0 w-full flex-col overflow-hidden bg-canvas text-ink',
        /*
         * At 768 the worker layout caps at a centred 430 px column rather
         * than stretching to tablet width — but the cap is a *tablet* rule,
         * and width alone cannot tell a tablet from a phone lying on its
         * side. A 844x390 handset satisfies `md:` and got the 430 px column
         * with black either side, on the one screen a worker holds sideways
         * to read a wide row.
         *
         * The height clause is the discriminator: a tablet in landscape is
         * still ~800 px tall, a phone in landscape is ~390. Portrait is
         * untouched (a 390x844 phone fails the width clause exactly as it
         * did), and so is every real tablet.
         */
        'mx-auto',
        '[@media(min-width:768px)_and_(min-height:600px)]:max-w-worker-column',
        '[@media(min-width:768px)_and_(min-height:600px)]:border-x',
        '[@media(min-width:768px)_and_(min-height:600px)]:border-hairline',
        className,
      )}
    >
      <a
        href="#worker-main"
        className={cn(
          'sr-only focus:not-sr-only focus:absolute focus:start-2 focus:top-2 focus:z-50',
          'focus:rounded-control focus:bg-surface focus:px-3 focus:py-2 focus:text-body',
          // Once it is visible it is a control on a worker surface, so it owes
          // the same 48 px a punch key does. `not-sr-only` restores
          // `position: static; display: block`, which measured ~40 px on the
          // shipped 15 px type; the flex box is what makes the floor real
          // rather than making the text taller.
          'focus:inline-flex focus:min-h-touch focus:items-center',
        )}
      >
        {skipLabel}
      </a>

      <header
        className={cn(
          'flex shrink-0 items-center justify-between gap-3 bg-chassis px-4 pt-safe-top',
          'min-h-rail sideways:min-h-rail-short',
        )}
      >
        <Wordmark
          label={brandLabel}
          {...(brandSrc !== undefined ? { src: brandSrc } : {})}
          className="text-ink-inverse-muted"
        />
        {clock !== undefined ? (
          <Readout scale="clock" unit={clock.zone} className="text-ink-inverse-muted">
            {clock.time}
          </Readout>
        ) : null}
      </header>

      <ConnectivityBand
        online={online}
        queueCount={queueCount}
        stateLabel={connectivityLabels.state}
        {...(connectivityLabels.queue !== undefined ? { queueLabel: connectivityLabels.queue } : {})}
        {...(connectivityLabels.clear !== undefined ? { clearLabel: connectivityLabels.clear } : {})}
        className="shrink-0"
      />

      {banner !== undefined ? <div className="shrink-0">{banner}</div> : null}

      <main id="worker-main" className="min-h-0 flex-1 overflow-y-auto px-3.5 py-3">
        {children}
      </main>

      {primaryAction !== undefined ? (
        <div className="shrink-0 px-3.5 pb-3">{primaryAction}</div>
      ) : null}

      <nav
        aria-label={navLabel}
        className="shrink-0 border-t border-hairline bg-chassis pb-safe-bottom"
      >
        <ul className="flex items-stretch">
          {nav.map((item) => {
            const Icon = item.icon;
            const className2 = cn(
              'flex min-h-nav w-full flex-col items-center justify-center gap-1 px-1',
              'transition-colors dur-state ease-out',
              'focus-visible:outline-focus-inverse',
              item.current === true
                ? 'border-t-[3px] border-brand text-brand'
                : 'border-t-[3px] border-transparent text-ink-inverse-muted',
            );
            const body = (
              <>
                <Icon {...decorativeIcon('xl')} />
                {/*
                  * Never icon-only: the label is 13 px (`text-body-compact`,
                  * the 15/13 px phone floor) and always present, and it is
                  * SENTENCE case. Every worker artboard draws "Pointage", not
                  * "POINTAGE": in a dark plant an all-caps word loses the
                  * ascender/descender silhouette that lets a tired eye read it
                  * without focusing. `text-label` is 11 px and reserved for
                  * column heads and eyebrows — under the phone floor for a tab
                  * a worker reads all night.
                  */}
                <span className="text-body-compact">{item.label}</span>
              </>
            );

            return (
              <li key={item.key} className="min-w-0 flex-1">
                {renderNavLink?.(item, className2, body) ?? (
                  <a
                    href={item.href}
                    aria-current={item.current === true ? 'page' : undefined}
                    className={className2}
                  >
                    {body}
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}
